import { useTranslation } from 'react-i18next'
import type { AuthTokens } from '@dorixona/api'
import type { TelegramPending } from '../../app/session'
import { useSupportPhone } from '../../app/settings'
import { BackButton } from '../../ui/BackButton'
import type { AuthApi } from './api'
import { PhoneStep } from './PhoneStep'
import { CodeStep } from './CodeStep'
import { RegisterStep } from './RegisterStep'
import { useAuthFlow } from './useAuthFlow'

export interface AuthScreenProps {
  api: AuthApi
  onAuthenticated: (tokens: AuthTokens) => void
  /** Мини-апп не вошёл сам — продолжить с недостающего шага (BR-39). */
  telegram?: TelegramPending | null
}

/**
 * Экран входа. Шаги сменяют друг друга переездом, как экраны нативного
 * приложения: вперёд — справа, назад — слева (auth.css, роль --dx-lazy).
 */
export function AuthScreen({ api, onAuthenticated, telegram = null }: AuthScreenProps) {
  const { t } = useTranslation(['auth', 'common'])
  const flow = useAuthFlow({ api, onAuthenticated, telegram })
  const support = useSupportPhone()
  // Незнакомый код ошибки не должен утекать в интерфейс: показываем общий текст.
  const error = flow.errorCode ? t([`errors:${flow.errorCode}`, 'errors:internal']) : undefined

  return (
    <section className="screen auth-wrap" data-step={flow.step}>
      <div className="mhead">
        {/* На первом шаге стрелка уводит со входа обратно, дальше — шаг назад. */}
        <BackButton to="/" onBack={flow.step === 'phone' ? undefined : flow.back} />
        {/* Язык выбирают на приветствии и в профиле — вход его не спрашивает. */}
      </div>

      <div key={flow.step}>
        {flow.step === 'phone' ? (
          <PhoneStep
            phone={flow.phone}
            onPhoneChange={flow.setPhone}
            onSubmit={flow.requestCode}
            pending={flow.pending}
            error={error}
            onShareContact={flow.canShare ? flow.shareContact : undefined}
            sharing={flow.sharing}
          />
        ) : null}

        {flow.step === 'code' ? (
          <CodeStep
            phone={flow.phone}
            code={flow.code}
            onCodeChange={flow.setCode}
            onSubmit={flow.submitCode}
            onResend={flow.requestCode}
            resendIn={flow.resendIn}
            pending={flow.pending}
            error={error}
          />
        ) : null}

        {flow.step === 'register' ? (
          <RegisterStep
            firstName={flow.firstName}
            onSubmit={(form) => {
              flow.submitRegistration({
                firstName: form.firstName,
                lastName: form.lastName,
                gender: form.gender,
                birthDate: form.birthDate,
              })
            }}
            pending={flow.pending}
            error={error}
          />
        ) : null}
      </div>

      {/* Не приходит код или не получается войти — звонок в поддержку, на любом шаге. */}
      {support ? (
        <p className="auth-note auth-support">
          {t('auth:support')}{' '}
          <a className="auth-link num" href={support.href}>
            {support.text}
          </a>
        </p>
      ) : null}
    </section>
  )
}
